import Vue from 'vue'
import Vuex from 'vuex'
import LogRocket from 'logrocket';
import createPlugin from 'logrocket-vuex';
import AJAX from "../utils/AJAX";
import getLocation from "../utils/get-location";
import appConfig from '../../application-configuration';
import Storage from "../utils/Storage";
import modules from './modules';
import router from '@router';
import { listenClientKey } from "@store/FirebaseListeners";
import Analytics from "@utils/Analytics";

const logrocketPlugin = createPlugin(LogRocket);

Vue.use(Vuex);

const queueKey = Storage.getItem(Storage.CONST__QUEUE_KEY);

const isRemoteOnlyNight = () => {
  // ? No need for this to actually be calculated, as I know there won't be another remote night this year (2025)
  return false;
  // const now = new Date();
  // // Convert to Adelaide time (ACDT, UTC+10:30)
  // const adelaideOffset = 10.5 * 60; // minutes
  // const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  // const adelaideTime = new Date(utc + (adelaideOffset * 60000));
  // return adelaideTime.getDate() === 10 && adelaideTime.getMonth() === 11; // December is 11 (0-indexed)
};

export default new Vuex.Store({
  modules,
  state: {
    lightsStatus: 'checking',
    remoteOnly: isRemoteOnlyNight(),
    isRemote: false,
    isLocal: false,
    paramCode: '',
    queueLength: 0,
    isInQueue: false,
    isRequestingControl: !!queueKey || false, // Handles issues when user refreshes page while in queue or in control. Displays loading animation until Firebase tells device where it is
    isInControl: false,
    queuePosition: null,
    key: queueKey || null
  },
  mutations: {
    setProperty(state, { property, value }) {
      state[property] = value;
    }
  },
  actions: {

    async requestControl({ getters, commit, dispatch }) {
      if (getters.isRequestingControl) {
        // If the user is already requesting control, no point letting them do it again.
        // This will actually prevent the user from tapping the Take Control button if they accidentally refresh the page
        return;
      }
      // console.log(`requestControl`);
      commit('setProperty', { property: 'isRequestingControl', value: true });
      // Pass the code that the user provided in the query string.
      // If it exists, then we're going to assume the best, and that they've been given access to control
      // the lights without being at the house..
      let currentPosition;
      if (getters.isLocal) {
        // paramCode is here for future use. Not using just yet..
        currentPosition = { latitude: -35.0165918, longitude: 138.6096444, code: getters.paramCode };
      } else {
        currentPosition = await getLocation();
      }
    
      // Send request to Firebase to request control - send users position information
      const response = await AJAX.post('requestTakeControl', { currentPosition });
      const { body: { key = null, control = false, queued = false, tooFar = false, position = null, lightsOff = false } } = response;

      commit('setProperty', { property: 'isRequestingControl', value: false });

      if (lightsOff) {
        dispatch('alert', { message: `Unable to take control, the lights are off!`, type: 'info' });
        return;
      }

      if (tooFar) {
        dispatch('alert', { message: `Unable to take control, too far from house.`, type: 'info' });

        // This will likely not be set, but in the case that someone is trying to sneak by guessing the query string,
        // this is at least going to make life a little bit more annoying.. Not going to stop them by anymeans,
        // and may not even slow them down, but it's something, and maybe I can just use it as a breadcrumb for where
        // to update code in the future
        localStorage.removeItem('queryStringCode');
        return;
      }

      commit('setProperty', { property: 'isInControl', value: control });
      Storage.setItem(Storage.CONST__QUEUE_KEY, key);
      commit('setProperty', { property: 'key', value: key });
      LogRocket.identify(key);
      listenClientKey(key);

      if (queued) {
        router.push({ path: '/queue' });
      }

      if (control) {
        router.push({ path: '/control' });
      }
    },

    SET_LIGHTS_STATUS({ commit }, payload) {
      const { status } = payload;
      commit('setProperty', { property: 'lightsStatus', value: status })
    },

    SET_QUEUE_LENGTH({ commit }, payload) {
      const { length } = payload;
      commit('setProperty', { property: 'queueLength', value: length });
    },

    ABANDON_QUEUE({ commit, getters }) {
      Analytics.FireFeatureUsed('abandon_queue', getters.clientKey)
      // console.log(`ABANDON_QUEUE`);
      commit('setProperty', { property: 'isInQueue', value: false });
      commit('setProperty', { property: 'isInControl', value: false });
      router.push({ path: '/' });

      // console.log(getters.clientKey);
      Storage.removeItem(Storage.CONST__QUEUE_KEY);
      AJAX.post('abandonQueue', {
        key: getters.clientKey
      });
    },

    SET_CLIENT_KEY({ commit }, payload) {
      // console.log(`SET_CLIENT_KEY`, payload);
      const { key } = payload;
      commit('setProperty', { property: 'key', value: key });
    },

    GRANT_CONTROL({ commit, getters }) {
      Analytics.FireFeatureUsed('abandon_queue', getters.clientKey);
      // console.log(`GRANT_CONTROL`);
      commit('setProperty', { property: 'isInControl', value: true });
      commit('setProperty', { property: 'isInQueue', value: false });
      if (router.history.current.name !== 'control') {
        router.push({ path: '/control' });
      }
    },

    REVOKE_CONTROL({ commit, dispatch, getters }) {
      Analytics.FireFeatureUsed('revoke_control', getters.clientKey);
      // console.log(`REVOKE_CONTROL`);
      commit('setProperty', { property: 'isInControl', value: false });
      commit('setProperty', { property: 'key', value: '' });
      dispatch('SET_IS_TRACKING_TIMER', { tracking: false });
      Storage.removeItem(Storage.CONST__QUEUE_KEY);
      commit('setProperty', { property: 'isRequestingControl', value: false });

      if (router.history.current.name !== 'take-control') {
        router.push({ path: '/finished?queue-advanced' });
      }
    },

    YIELD_CONTROL({ commit, getters }) {
      Analytics.FireFeatureUsed('yield_control', getters.clientKey);

      Storage.removeItem(Storage.CONST__QUEUE_KEY);
      if (!getters.isInControl) {
        router.push({ path: '/finished' });
        return;
      }
      // console.log(`YIELD_CONTROL`);

      AJAX.post('yieldControl', { key: getters.clientKey });
      if (router.history.current.name !== 'take-control') {
        commit('setProperty', { property: 'isRequestingControl', value: false });
        commit('setProperty', { property: 'isInControl', value: false });
        router.push({ path: '/finished' });
      }
    },

    IS_IN_QUEUE({ commit }, payload) {
      // console.log(`IS_IN_QUEUE`, payload);
      const { position } = payload;
      commit('setProperty', { property: 'isInControl', value: false });
      commit('setProperty', { property: 'isInQueue', value: true });
      commit('setProperty', { property: 'queuePosition', value: position });
      if (router.history.current.name !== 'queue') {
        router.push({ path: '/queue' });
      }
    },

    /**
     * Likely called after a user has refreshed their device, whilst either being in control, or being in the queue.
     * The UI is updated to look like it's loading for those users, to prevent them from clicking the Take Control button whilst
     * Firebase is getting back to them to let them know they're in control, or which position of the queue they're in
     */
    NOT_IN_QUEUE({ commit }) {
      commit('setProperty', { property: 'isInQueue', value: false }); // Shouldn't be needed, but just in case
      commit('setProperty', { property: 'isRequestingControl', value: false }); // Firebase has finished checking if this person is in the queue
    },

    PURGE_QUEUE() {
      AJAX.post('purgeQueue');
    },

    SET_IS_REMOTE({ commit }, payload) {
      const { isRemote } = payload;
      commit('setProperty', { property: 'isRemote', value: isRemote });
    },

    /**
     * When a user scans the QR code, they're obviously at the house, so we will set them as
     * local to skip geolocation
     */
    SET_IS_LOCAL({ commit }, payload) {
      const { isLocal } = payload;
      commit('setProperty', { property: 'isLocal', value: isLocal });
    },

    SET_CODE({ commit }, payload) {
      const { code } = payload;
      commit('setProperty', { property: 'paramCode', value: code });
    }
  },
  getters: {
    lightsStatus: state => state.lightsStatus,
    queueLength: state => state.queueLength,
    remoteOnly: state => state.remoteOnly,
    isRemote: state => state.isRemote,
    isLocal: state => state.isLocal,
    code: state => state.paramCode,
    isInControl: state => state.isInControl,
    isRequestingControl: state => state.isRequestingControl,
    isInQueue: state => state.isInQueue,
    clientKey: state => state.key,
    queuePosition: state => state.queuePosition
  },
  plugins: [logrocketPlugin]
})
